import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-4 p-4 rounded-lg hover:bg-background-100/70 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <p className="mt-1 text-xs text-foreground-500">
            {article.publishDate} · {article.readTime}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden cursor-pointer bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-colors"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs text-foreground-500">{article.publishDate}</span>
            <span className="text-xs text-foreground-400">·</span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
          </div>
          <h3 className="text-lg font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-lg overflow-hidden cursor-pointer bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-colors"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs text-foreground-500">{article.publishDate}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {article.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 text-xs bg-secondary-100 text-secondary-700 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}